import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { toast } from 'sonner';
import { TreePine, Users, GraduationCap, Heart, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface MilestoneData {
  id: string;
  projectName: string;
  projectType: string;
  contributionCount: number;
  milestone: number;
  impact: string;
}

export function SocialMilestoneNotification() {
  const { user } = useAuth();
  const { isSubscribed } = usePushNotifications();
  const [milestone, setMilestone] = useState<MilestoneData | null>(null);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    if (user) {
      checkForMilestones();
    }
  }, [user]);

  const checkForMilestones = async () => {
    if (!user) return;

    try {
      // Get customer's contributions using secure RPC
      const { data: customerData, error: customerError } = await supabase.rpc('get_or_create_customer_profile', {
        p_user_id: user.id,
        p_email: user.email || '',
        p_full_name: user.user_metadata?.full_name || 'Kunde'
      })

      if (customerError || !customerData || customerData.length === 0) {
        console.error('Error fetching customer data:', customerError)
        return
      }

      const customer = customerData[0]

      // Get recent contributions that might have hit milestones
      const { data: contributions } = await supabase
        .from('customer_social_contributions')
        .select(`
          contribution_count,
          social_project_id,
          last_contribution_at,
          social_projects (
            name,
            project_type
          )
        `)
        .eq('customer_id', customer.id)
        .gte('last_contribution_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()); // Last 24 hours

      if (!contributions || contributions.length === 0) return;

      // Check for milestone achievements
      for (const contribution of contributions) {
        const count = contribution.contribution_count;
        const milestones = [1, 5, 10, 25, 50, 100];
        
        for (const milestone of milestones) {
          if (count === milestone) {
            const impact = generateImpactMessage(contribution.social_projects.project_type, milestone);
            
            setMilestone({
              id: contribution.social_project_id,
              projectName: contribution.social_projects.name,
              projectType: contribution.social_projects.project_type,
              contributionCount: count,
              milestone,
              impact
            });
            
            setShowDialog(true);
            
            // Send push notification if subscribed
            if (isSubscribed) {
              await sendMilestoneNotification(contribution.social_projects.name, milestone, impact);
            }
            
            return; // Show only one milestone at a time
          }
        }
      }
    } catch (error) {
      console.error('Error checking milestones:', error);
    }
  };

  const generateImpactMessage = (projectType: string, count: number): string => {
    switch (projectType) {
      case 'trees':
        if (count === 1) return 'Sie haben Ihren ersten Baum gepflanzt!';
        if (count <= 10) return `${count} Bäume tragen bereits zu einem gesünderen Klima bei!`;
        if (count <= 50) return `${count} Bäume! Das entspricht etwa ${Math.round(count * 22)} kg CO₂ pro Jahr!`;
        return `${count} Bäume! Ein ganzer kleiner Wald dank Ihrer Unterstützung!`;
      case 'families':
        if (count === 1) return 'Sie haben Ihrer ersten Familie geholfen!';
        if (count <= 5) return `${count} Familien haben durch Sie Unterstützung erhalten!`;
        return `${count} Familien - das sind etwa ${count * 4} Menschen, denen Sie geholfen haben!`;
      case 'education':
        if (count === 1) return 'Sie haben Ihr erstes Bildungsprojekt unterstützt!';
        if (count <= 20) return `${count} Bildungsinitiativen wurden durch Sie gefördert!`;
        return `${count} Bildungsprojekte - das entspricht etwa ${count * 50} Kindern mit besseren Chancen!`;
      default:
        return `${count} Beiträge zu sozialen Projekten - herzlichen Dank!`;
    }
  };

  const sendMilestoneNotification = async (projectName: string, milestone: number, impact: string) => {
    try {
      await supabase.functions.invoke('send-push-notification', {
        body: {
          title: '🎉 Meilenstein erreicht!',
          message: `${milestone}. Beitrag zu "${projectName}" - ${impact}`,
          targetType: 'user',
          targetUserId: user?.id,
          priority: 'high'
        }
      });
    } catch (error) {
      console.error('Error sending milestone notification:', error);
    }
  };

  const getProjectIcon = (type: string) => {
    switch (type) {
      case 'trees': return TreePine;
      case 'families': return Heart;
      case 'education': return GraduationCap;
      default: return Heart;
    }
  };

  const shareAchievement = async () => {
    if (!milestone) return;

    const text = `🎉 Ich habe gerade meinen ${milestone.milestone}. Beitrag zu "${milestone.projectName}" geleistet! ${milestone.impact}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Soziales Engagement Meilenstein',
          text: text,
          url: window.location.origin
        });
      } catch (error) {
        console.log('Sharing cancelled');
      }
    } else {
      // Fallback: Copy to clipboard
      await navigator.clipboard.writeText(text);
      toast.success('Nachricht in die Zwischenablage kopiert!');
    }
  };

  if (!milestone) return null;

  const Icon = getProjectIcon(milestone.projectType);

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-center">
            <Icon className="h-6 w-6 text-primary" />
            Meilenstein erreicht! 🎉
          </DialogTitle>
          <DialogDescription className="text-center">
            <div className="space-y-4 mt-4">
              <div className="text-lg font-semibold text-foreground">
                {milestone.milestone}. Beitrag zu
              </div>
              <div className="text-xl font-bold text-primary">
                {milestone.projectName}
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">
                  {milestone.impact}
                </p>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex gap-2 mt-6">
          <Button 
            variant="outline" 
            onClick={shareAchievement}
            className="flex-1"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Teilen
          </Button>
          <Button 
            onClick={() => setShowDialog(false)}
            className="flex-1"
          >
            Schließen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}